"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, Droplets, Flame, MapPin, PhoneCall, Pipette, ShieldAlert, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const emergencyProblems = [{
  id: "p1",
  title: "Burst Pipe or Major Flood",
  description: "Uncontrolled water pouring inside walls or ceilings. Shut off main valve immediately.",
  urgency: "Critical (15-30 Min Dispatch)",
  path: "/problems/",
  iconName: "Droplets"
}, {
  id: "p2",
  title: "No Hot Water / Tank Leaking",
  description: "Complete loss of hot water or water pooling around tank base.",
  urgency: "High (Same Day / Immediate)",
  path: "/water-heater/",
  iconName: "Flame"
}, {
  id: "p3",
  title: "Whole-House Drain Backup",
  description: "Toilets gurgling, water coming up through tubs or floor drains.",
  urgency: "Immediate Response",
  path: "/services/",
  iconName: "AlertTriangle"
}, {
  id: "p4",
  title: "Severe Sewer Line Odor",
  description: "Sewer gas smell or standing waste water in yard or crawlspace.",
  urgency: "Urgent Priority",
  path: "/services/",
  iconName: "ShieldAlert"
}];
const serviceAreas = [{
  name: "Jonesboro (HQ)",
  status: "Local Hub",
  time: "15-30 min"
}, {
  name: "Riverdale",
  status: "Active Crews",
  time: "20-35 min"
}, {
  name: "Morrow",
  status: "Active Crews",
  time: "20-35 min"
}, {
  name: "Forest Park",
  status: "Active Crews",
  time: "25-40 min"
}, {
  name: "Stockbridge",
  status: "Active Crews",
  time: "25-40 min"
}, {
  name: "Fayetteville",
  status: "Active Crews",
  time: "30-45 min"
}];
const primaryServices = [{
  title: "24/7 Emergency Repair",
  desc: "Rapid response plumbing crews equipped for live pipe bursts, valve failures, and catastrophic leaks.",
  features: ["Under 45-min arrival target", "Fully stocked service vans", "No overtime surcharge"],
  cta: "Emergency Dispatch",
  path: "/services/"
}, {
  title: "Water Heater Emergency",
  desc: "Fast repair and emergency replacements for standard gas, electric, and tankless systems.",
  features: ["All major brand parts in stock", "Leak containment & mitigation", "Same-day replacement options"],
  cta: "Water Heater Help",
  path: "/water-heater/"
}, {
  title: "Drain & Sewer Clearing",
  desc: "Hydro-jetting and motorized camera snaking for stubborn blockages and main line backups.",
  features: ["Video camera diagnostic", "Root & grease removal", "Main line cleanout access"],
  cta: "Clear Clogged Line",
  path: "/services/"
}];
export default function ServicesTriage() {
  const shouldReduceMotion = useReducedMotion();
  const getProblemIcon = (iconName: string) => {
    switch (iconName) {
      case "Droplets":
        return <Droplets className="size-5 text-destructive" />;
      case "Flame":
        return <Flame className="size-5 text-accent" />;
      case "AlertTriangle":
        return <AlertTriangle className="size-5 text-destructive" />;
      default:
        return <ShieldAlert className="size-5 text-accent" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="servicesandserviceareatriage" data-nav="light" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/60 px-3.5 py-1 text-xs font-semibold text-foreground">
            <span className="relative flex size-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-destructive" />
            </span>
            Jonesboro & Clayton County Emergency Triage
          </div>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Identify Your Issue, Dispatch Local Plumbers Now
          </h2>
          <p className="mt-3 text-base text-muted-foreground sm:text-lg">
            Select your emergency below for immediate response times, or call our 24/7 on-call master technicians directly in Jonesboro, GA.
          </p>
        </div>

        {/* Problem Triage Quick Select Bar */}
        <div className="mt-12">
          <div className="flex items-center justify-between gap-4 pb-4">
            <div className="flex items-center gap-2">
              <Zap className="size-5 text-accent" />
              <h3 className="text-lg font-bold text-foreground">Urgent Problem Triage</h3>
            </div>
            <Link to="/problems/" className="inline-flex items-center text-xs font-semibold text-primary hover:underline">
              View all diagnostic guides
              <ArrowRight className="ml-1 size-3.5" />
            </Link>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {emergencyProblems.map((problem, i) => <motion.div key={problem.id} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm transition-[border-color,box-shadow] hover:border-primary/50 hover:shadow-md">
                <div>
                  <div className="flex items-center justify-between">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                      {getProblemIcon(problem.iconName)}
                    </div>
                    <Badge variant="outline" className="border-border text-[11px] font-semibold text-muted-foreground">
                      {problem.urgency}
                    </Badge>
                  </div>
                  <h4 className="mt-4 text-base font-bold text-card-foreground group-hover:text-primary">
                    {problem.title}
                  </h4>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                    {problem.description}
                  </p>
                </div>

                <div className="mt-5 border-t border-border/60 pt-3">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between text-xs font-medium">
                    <Link to={problem.path}>
                      Triage This Issue
                      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Main Grid: Core Services + Service Area Status */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Related Core Service Cards */}
          <div className="lg:col-span-8">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-foreground">Core Plumbing Services</h3>
                <p className="text-xs text-muted-foreground">Comprehensive emergency solutions backed by licensed technicians</p>
              </div>
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                <Link to="/services/">
                  All Services
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {primaryServices.map((service, i) => <Card key={service.title} data-index={i} className="flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow]">
                  <CardHeader className="p-5 pb-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-2">
                      <Wrench className="size-4" />
                    </div>
                    <CardTitle className="text-base font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground leading-normal mt-1">
                      {service.desc}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="p-5 pt-0">
                    <Separator className="my-3 bg-border" />
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-2 text-xs text-muted-foreground">
                          <CheckCircle2 className="size-3.5 shrink-0 text-primary mt-0.5" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>

                  <CardFooter className="p-5 pt-0">
                    <Button asChild variant="default" size="sm" className="w-full text-xs">
                      <Link to={service.path}>
                        {service.cta}
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>)}
            </div>
          </div>

          {/* Service Area Status Box */}
          <div className="lg:col-span-4">
            <div className="flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-sm">
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-accent/20 text-accent-foreground">
                    <MapPin className="size-5 text-foreground" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-card-foreground">Local Coverage Area</h3>
                    <p className="text-xs text-muted-foreground">Jonesboro & Surrounding Towns</p>
                  </div>
                </div>

                <div className="mt-5 rounded-lg bg-muted/70 p-3.5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    <Clock className="size-4 text-primary" />
                    <span>Live GPS On-Road Dispatching</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Technicians staged across Clayton and neighboring counties for under 45-minute response.
                  </p>
                </div>

                <div className="mt-4 divide-y divide-border">
                  {serviceAreas.map((area, i) => <div key={area.name} data-index={i} className="flex items-center justify-between py-2.5 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="size-1.5 rounded-full bg-primary" />
                        <span className="font-semibold text-foreground">{area.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">{area.time}</span>
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-medium">
                          {area.status}
                        </Badge>
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="mt-6 border-t border-border pt-5">
                <Button asChild variant="outline" size="sm" className="w-full mb-3">
                  <Link to="/service-areas/">
                    Check Detailed Map & Coverage
                  </Link>
                </Button>
                <Button asChild variant="default" size="default" className="w-full font-bold shadow-sm">
                  <Link to="/contact/">
                    <PhoneCall className="mr-2 size-4" />
                    Request Emergency Dispatch
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Rapid Contact Banner */}
        <div className="mt-12 rounded-xl bg-primary p-6 text-primary-foreground shadow-md md:p-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="text-center md:text-left">
              <span className="inline-block rounded-md bg-white/15 px-2.5 py-0.5 text-xs font-semibold tracking-wide uppercase">
                24/7 Rapid Response
              </span>
              <h3 className="mt-2 text-2xl font-bold">Unsure which service you need?</h3>
              <p className="mt-1 text-sm text-primary-foreground/90 max-w-xl">
                Speak directly with an on-duty Jonesboro plumber to evaluate the leak or backup over the phone in under 60 seconds.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="secondary" size="lg" className="font-bold">
                <Link to="/contact/">
                  <PhoneCall className="mr-2 size-4" />
                  Call Local Dispatch
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/20 hover:bg-primary/40 hover:text-primary-foreground">
                <Link to="/brands/">
                  Supported Brands
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}